import { useEffect, useRef, useState } from 'react';
import { Stack, Box, LinearProgress, Typography, Button } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import ReplayIcon from '@mui/icons-material/Replay';
import { useQueryClient } from '@tanstack/react-query';

const SubmitContractRequestFeedback = ({
  mutationIsLoading,
  mutationIsSuccess,
  mutationResult,
  mutationIsError,
  resetRequestForm,
  retryRequest,
  refetchRequests,
}) => {
  const queryClient = useQueryClient();
  const [value, setValue] = useState(0);

  const loadingMessages = [
    'We’re submitting your request…',
    'Processing your files…',
    'Validating information…',
    'Finishing up',
  ];
  const STEP_MS = 3000;
  const [messageIndex, setMessageIndex] = useState(0);
  const [fade, setFade] = useState(true);
  const messageTimersRef = useRef([]);

  // progress animation
  useEffect(() => {
    let frame;
    if (mutationIsLoading) {
      setValue(0);
      const start = performance.now();
      const tick = (t) => {
        const elapsed = t - start;
        const estimateMs = 7000;
        const cap = 90;
        const pct = Math.min(cap, (elapsed / estimateMs) * cap);
        setValue(Math.round(pct));
        frame = requestAnimationFrame(tick);
      };
      frame = requestAnimationFrame(tick);
    } else {
      const from = value;
      const start = performance.now();
      const duration = 300;
      const ease = (x) => 1 - Math.pow(1 - x, 3);
      const step = (t) => {
        const k = Math.min(1, (t - start) / duration);
        const v = Math.round(from + (100 - from) * ease(k));
        setValue(v);
        if (k < 1) frame = requestAnimationFrame(step);
      };
      frame = requestAnimationFrame(step);
    }
    return () => cancelAnimationFrame(frame);
  }, [mutationIsLoading]);

  // refetch requests on mutationIsSuccess
  useEffect(() => {
    refetchRequests();
  }, [mutationIsSuccess]);

  useEffect(() => {
    messageTimersRef.current.forEach(clearTimeout);
    messageTimersRef.current = [];

    if (!mutationIsLoading) {
      setMessageIndex(0);
      return;
    }

    setMessageIndex(0);

    for (let i = 1; i < loadingMessages.length; i++) {
      const id = setTimeout(() => {
        setFade(false);
        setTimeout(() => {
          setMessageIndex(i);
          setFade(true);
        }, 300);
      }, i * STEP_MS);
      messageTimersRef.current.push(id);
    }

    return () => {
      messageTimersRef.current.forEach(clearTimeout);
      messageTimersRef.current = [];
    };
  }, [mutationIsLoading, loadingMessages.length]);

  return (
    <Box
      sx={{
        padding: 2,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        width: '100%',
      }}
    >
      {mutationIsLoading && (
        <Stack spacing={1}>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="center"
            spacing={1}
            sx={{
              transition: 'opacity 0.3s ease',
              opacity: fade ? 1 : 0,
              width: 300,
            }}
          >
            <Typography variant="body2" fontWeight={500} aria-live="assertive" aria-atomic="true">
              {loadingMessages[messageIndex]}
            </Typography>
          </Stack>
          <LinearProgress
            variant="determinate"
            value={value}
            sx={{
              borderRadius: 2,
              height: 8,
              [`& .MuiLinearProgress-bar`]: { borderRadius: 2 },
            }}
          />
        </Stack>
      )}
      {mutationIsSuccess && (
        <Stack direction="column" alignItems="center" spacing={2}>
          {mutationResult === true ? (
            <>
              <CheckCircleIcon color="success" />
              <Typography variant="body2" fontWeight={500}>
                We’ve successfully submitted your request.
              </Typography>
              <Stack direction="row" gap={2}>
                <Button onClick={resetRequestForm}>New Request</Button>
              </Stack>
            </>
          ) : (
            <>
              <ErrorIcon color="error" />
              <Typography variant="body2" fontWeight={500}>
                Request failed.
              </Typography>
              <Stack direction="row" gap={2}>
                <Button onClick={retryRequest} endIcon={<ReplayIcon />}>
                  Retry
                </Button>
                <Button onClick={resetRequestForm}>New Request</Button>
              </Stack>
            </>
          )}
        </Stack>
      )}
      {mutationIsError && (
        <Stack direction="column" alignItems="center" spacing={2}>
          <ErrorIcon color="error" />
          <Typography variant="body2" fontWeight={500}>
            Something went wrong.
          </Typography>
          <Stack direction="row" gap={2}>
            <Button onClick={retryRequest} endIcon={<ReplayIcon />}>
              Retry
            </Button>
            <Button onClick={resetRequestForm}>New Request</Button>
          </Stack>
        </Stack>
      )}
    </Box>
  );
};

export default SubmitContractRequestFeedback;
